import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "/assets/logo-le-bleu-des-champs.png";

const navLinks = [
  { name: 'Accueil', path: '/' },
  { name: 'Services', path: '/services' },
  { name: 'Soumission', path: '/soumission' },
  { name: 'À propos', path: '/a-propos' },
];

export const PublicLayout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const menuButtonRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;

    mobileMenuRef.current?.querySelector('a')?.focus();
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };

    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [mobileMenuOpen]);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top bar */}
      <div className="bg-[#1a3a5c] text-white py-2 px-4 hidden md:block">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href="tel:+14504693483" className="flex items-center gap-2 hover:text-[#00d4ff] transition-colors">
              <Phone className="w-4 h-4" />
              <span>(450) 469-3483</span>
            </a>
            <a href="mailto:lebleudeschamps@bellnet.ca" className="flex items-center gap-2 hover:text-[#00d4ff] transition-colors">
              <Mail className="w-4 h-4" />
              <span>lebleudeschamps@bellnet.ca</span>
            </a>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            <span>Saint-Césaire, Québec</span>
          </div>
        </div>
      </div>

      {/* Main header */}
      <header className="sticky top-0 z-50 glass shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src={LOGO_URL} 
                alt="Le Bleu des Champs" 
                className="h-14 w-auto"
              />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'bg-[#00b4d8] text-white'
                      : 'text-[#1a3a5c] hover:bg-slate-100'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/connexion">
                <Button 
                  variant="outline" 
                  className="ml-4 rounded-full border-2 border-[#1a3a5c] text-[#1a3a5c] hover:bg-[#1a3a5c] hover:text-white"
                  data-testid="login-btn"
                >
                  Connexion
                </Button>
              </Link>
            </nav>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              ref={menuButtonRef}
              data-testid="mobile-menu-btn"
              aria-label={mobileMenuOpen ? 'Fermer le menu principal' : 'Ouvrir le menu principal'}
              aria-expanded={mobileMenuOpen}
              aria-controls="menu-mobile"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div ref={mobileMenuRef} id="menu-mobile" className="md:hidden glass border-t border-slate-200 animate-slide-in">
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'bg-[#00b4d8] text-white'
                      : 'text-[#1a3a5c] hover:bg-slate-100'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/connexion"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-xl text-base font-medium text-center border-2 border-[#1a3a5c] text-[#1a3a5c] mt-4"
              >
                Connexion
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#1a3a5c] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Logo & description */}
            <div className="md:col-span-2">
              <img src={LOGO_URL} alt="Le Bleu des Champs" className="h-12 mb-4 brightness-0 invert" />
              <p className="text-slate-300 max-w-md">
                Depuis 1977, Le Bleu des Champs offre des services de climatisation, chauffage, 
                ventilation et réparation d'électroménagers à Saint-Césaire et ses environs.
              </p>
            </div>

            {/* Quick links */}
            <div>
              <h4 className="font-semibold mb-4 text-[#00d4ff]">Liens rapides</h4>
              <ul className="space-y-2">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link to={link.path} className="text-slate-300 hover:text-white transition-colors">
                      {link.name}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to="/politique-confidentialite" className="text-slate-300 hover:text-white transition-colors">
                    Politique de confidentialité
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-4 text-[#00d4ff]">Contact</h4>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#00b4d8]" />
                  <span>(450) 469-3483</span>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#00b4d8]" />
                  <span>lebleudeschamps@bellnet.ca</span>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-[#00b4d8] mt-1" />
                  <span>831, route 112, Saint-Césaire<br />Québec, Canada</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-600 mt-8 pt-8 text-center text-slate-400 text-sm">
            <p>© {new Date().getFullYear()} Le Bleu des Champs. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
